/**
 * The eight Form 4180 interview sections (03-17). Each renders inside the single
 * wizard Formik (so autosave sees the whole `data` blob); gated reveals read
 * sibling values via `useFormikContext`. Hidden fields keep their values (toggling
 * a gate off never clears them) — matching the legacy + the mapper's behavior.
 * Sensitive fields (EIN, bank/account, EFTPS PIN holders) are never logged.
 */

import { useFormikContext } from "formik";
import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { Client } from "@/features/clients/types";
import {
  FormikCell,
  FormikCheckbox,
  FormikRadioGroup,
  FormikRadioYN,
  FormikTextarea,
} from "@/features/forms/components/formik-controls";
import type { Form4180Data } from "../fields";
import { SEC2_DUTIES } from "../fields";
import { BanksList, EftpsList, IndividualsList, PositionsList } from "./list-sections";

const YNU = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "U", label: "Don't know" },
];
const YND = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "D", label: "Don't know" },
];

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function Question({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <span className="text-foreground text-sm font-medium">{label}</span>
      {children}
    </Card>
  );
}

function Text({ name, label, ...rest }: { name: string; label: string } & Record<string, unknown>) {
  return (
    <Field label={label} htmlFor={name}>
      <FormikCell name={name} {...rest} />
    </Field>
  );
}

// --- Section 1 — Header & Position ------------------------------------------

export function ExciseSection1({ client }: { client: Client }) {
  const { values } = useFormikContext<Form4180Data>();
  const isMarried = client.maritalStatus === "MARRIED";
  const forSpouse = values.for_who === "S";
  const spouseName = [client.spouseFirstName, client.spouseLastName].filter(Boolean).join(" ");
  const headerName = forSpouse ? spouseName || "(spouse)" : client.name;

  return (
    <div className="flex flex-col gap-6">
      {isMarried && (
        <Question label="Generate this 4180 for">
          <FormikRadioGroup
            name="for_who"
            aria-label="Generate for"
            options={[
              { value: "T", label: "Taxpayer" },
              { value: "S", label: "Spouse" },
            ]}
          />
        </Question>
      )}

      <Card className="flex flex-col gap-2 p-5">
        <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Name &amp; Address (from the client record)
        </span>
        <p className="text-foreground text-sm font-medium">{headerName}</p>
        <p className="text-muted-foreground text-sm">
          The name, SSN, phone numbers, and mailing address are filled automatically
          from the client record when the PDF is generated — they aren't edited here.
        </p>
      </Card>

      <Text name="business_name" label="6. Name of business" maxLength={120} />
      <Text name="ein_no" label="Employer Identification Number (EIN)" maxLength={10} autoComplete="off" />
      <Text name="job_title" label="8. Job title / association with the business" maxLength={100} />
    </div>
  );
}

// --- Section 2 — Responsibility & Authority ---------------------------------

export function ExciseSection2({ submissionId }: { submissionId: string }) {
  const { values } = useFormikContext<Form4180Data>();
  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground text-sm">
        1. State whether you performed any of these duties for the business and the
        time periods during which you performed them.
      </p>
      {SEC2_DUTIES.map((duty) => (
        <Question key={duty.key} label={duty.label}>
          <FormikRadioYN name={duty.key} aria-label={duty.label} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Dates from" htmlFor={`${duty.key}_from`}>
              <FormikCell name={`${duty.key}_from`} type="date" />
            </Field>
            <Field label="Dates to" htmlFor={`${duty.key}_to`}>
              <FormikCell name={`${duty.key}_to`} type="date" />
            </Field>
          </div>
          <FormikCheckbox name={`${duty.key}_chk`} label="See explanation" />
          {values[`${duty.key}_chk`] === true && (
            <FormikTextarea name={`${duty.key}_exp`} aria-label="Explanation" rows={3} />
          )}
        </Question>
      ))}

      <Question label="h. Could other individuals do any of the above?">
        <FormikRadioGroup name="other_indvidual" aria-label="Other individuals" options={YND} />
        {values.other_indvidual === "Y" && <IndividualsList submissionId={submissionId} />}
      </Question>

      <Question label="i. Do you have signature authority or PIN assignment on business bank accounts?">
        <FormikRadioGroup name="assign_bank" aria-label="Signature authority" options={YND} />
        {values.assign_bank === "Y" && <BanksList submissionId={submissionId} />}
      </Question>
    </div>
  );
}

// --- Section 3 — Interview & Handouts ---------------------------------------

export function ExciseSection3() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Text name="person_interviewed_date" label="Date person interviewed" type="date" />
        <Text name="person_interviewer_date" label="Date interviewer signed" type="date" />
        <Text name="completed_interview_date" label="Date copy of completed interview given" type="date" />
      </div>
      <Question label="Taxpayer statement obtained (on page 4)?">
        <FormikRadioYN name="taxpayer_stmt" aria-label="Taxpayer statement" />
      </Question>
      <Question label="Interview continued on subsequent pages?">
        <FormikRadioYN name="intr_cont_sub_page" aria-label="Interview continued" />
      </Question>
      <Question label="Interview handouts given (check if given)">
        <FormikCheckbox name="notice_609" label="Notice 609 — Privacy Act Notice" />
        <FormikCheckbox name="notice_784" label="Notice 784 — Could You be Personally Liable…" />
      </Question>
    </div>
  );
}

// --- Section 4 — EFTPS & Corporate Positions --------------------------------

export function ExciseSection4({ submissionId }: { submissionId: string }) {
  const { values } = useFormikContext<Form4180Data>();
  return (
    <div className="flex flex-col gap-6">
      <Question label="1. Corporate positions">
        <PositionsList submissionId={submissionId} />
      </Question>

      <Question label="2. Did/does the business use EFTPS to make Federal Tax Deposits or payments?">
        <FormikRadioGroup name="use_eftps" aria-label="Use EFTPS" options={YNU} />
        {values.use_eftps === "Y" && (
          <Text name="assgn_eftps_pwd" label="To whom are the PINs/passwords assigned?" maxLength={50} />
        )}
      </Question>

      <Question label="3. Other than EFTPS, does the business do any other banking electronically?">
        <FormikRadioGroup name="oth_eftps" aria-label="Other electronic banking" options={YNU} />
        {values.oth_eftps === "Y" && (
          <div className="flex flex-col gap-4">
            <Text name="where_oth_eftps_pwd" label="Where" maxLength={50} />
            <Text name="assgn_oth_eftps_pwd" label="To whom assigned" maxLength={50} />
            <EftpsList submissionId={submissionId} />
          </div>
        )}
      </Question>

      <Question label="4. Does the business file Form 941 electronically?">
        <FormikRadioGroup name="fill_form_941" aria-label="File 941 electronically" options={YNU} />
        {values.fill_form_941 === "N" && (
          <Text name="auth_sign_941" label="Who is authorized to sign Form 941?" maxLength={40} />
        )}
        {values.fill_form_941 === "Y" && (
          <Text name="file_ret_941" label="Who files the returns electronically?" maxLength={40} />
        )}
      </Question>
    </div>
  );
}

// --- Section 5 — Knowledge & Actions ----------------------------------------

export function ExciseSection5() {
  const { values } = useFormikContext<Form4180Data>();
  return (
    <div className="flex flex-col gap-6">
      <Question label="1. Were any financial obligations of the business paid while the delinquent taxes were increasing?">
        <FormikRadioGroup name="oblg_bsn_paid" aria-label="Obligations paid" options={YNU} />
        {values.oblg_bsn_paid === "Y" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Text name="oblg_bsn_paid_which" label="Which obligations?" maxLength={50} />
            <Text name="oblg_bsn_paid_who" label="Who authorized them?" maxLength={50} />
          </div>
        )}
      </Question>
      <Question label="2. Were all or a portion of the payrolls met?">
        <FormikRadioGroup name="payroll_port_met" aria-label="Payrolls met" options={YNU} />
        {values.payroll_port_met === "Y" && (
          <Text name="payroll_port_met_auth" label="Who authorized?" maxLength={50} />
        )}
      </Question>
      <Question label="3. Did any person or organization provide funds to pay net corporate payroll?">
        <FormikRadioGroup name="org_provd_fund" aria-label="Provided funds" options={YNU} />
        {values.org_provd_fund === "Y" && (
          <FormikTextarea name="org_provd_fund_exp" aria-label="Explain" rows={3} />
        )}
      </Question>
      <Question label="4. When and how did you first become aware of the unpaid taxes?">
        <FormikTextarea name="aware_unpaid_tax" aria-label="When/how aware" rows={3} />
      </Question>
      <Question label="5. What actions did you attempt to see that the taxes were paid?">
        <FormikTextarea name="action_attemp" aria-label="Actions attempted" rows={3} />
      </Question>
      <Question label="6. Were discussions ever held regarding nonpayment of the taxes?">
        <FormikRadioGroup name="desc_nonpay_tax" aria-label="Discussions held" options={YNU} />
        {values.desc_nonpay_tax === "Y" && (
          <FormikTextarea name="desc_nonpay_tax_identify" aria-label="Identify who attended" rows={3} />
        )}
      </Question>
      <Question label="7. Who handled IRS contacts (calls, correspondence, visits)?">
        <FormikTextarea name="handle_irs_contact" aria-label="Who handled IRS contacts" rows={2} />
      </Question>
      <Question label="8. When did these contacts take place, and what were the results?">
        <FormikTextarea name="handle_irs_contact_result" aria-label="Contact results" rows={2} />
      </Question>
    </div>
  );
}

// --- Section 6 — Third-Party Payer Arrangements -----------------------------

export function ExciseSection6() {
  const { values } = useFormikContext<Form4180Data>();
  return (
    <div className="flex flex-col gap-6">
      <Question label="Did you use a third-party payer (e.g. a payroll service)? (Yes = complete Section 6A)">
        <FormikRadioGroup name="third_party_payer" aria-label="Used a third-party payer" options={YNU} />
      </Question>

      {values.third_party_payer === "Y" && (
        <Card className="flex flex-col gap-4 p-5">
          <span className="text-foreground text-sm font-semibold">6A — Third-Party Payer Arrangements</span>
          <Text name="psp_sign_service_cont" label="1. Who signed the service contract?" maxLength={30} />
          <Text name="psp_busn_handle" label="2. Who in the business handled the contact?" maxLength={30} />
          <Field label="3. Who was your contact at the third-party payer?" htmlFor="psp_contact">
            <FormikTextarea name="psp_contact" rows={2} maxLength={255} />
          </Field>
          <Text name="psp_fund_avail" label="4. How were funds made available to the payer?" maxLength={30} />
          <Text name="psp_bank_name_acc" label="Name of bank(s) and account number(s)" maxLength={30} autoComplete="off" />
          <Field label="5. What actions did you take to verify filing / payment?" htmlFor="psp_verify_third_party">
            <FormikTextarea name="psp_verify_third_party" rows={2} />
          </Field>
          <Question label="6. Were funds available for the payer to use for payment of the taxes?">
            <FormikRadioYN name="psp_fund_avail_pay" aria-label="Funds available to payer" />
            {values.psp_fund_avail_pay === "Y" && (
              <FormikTextarea name="psp_fund_avail_pay_exp" aria-label="Explain transfer" rows={2} />
            )}
          </Question>
          <Question label="7. Were you aware the payer was not making the required payments?">
            <FormikRadioYN name="psp_not_make_req_pay" aria-label="Aware payer not paying" />
          </Question>
          <Question label="8. Did you receive IRS notices that returns/taxes were not filed or paid?">
            <FormikRadioYN name="psp_recv_irs_notice" aria-label="Received IRS notices" />
          </Question>
        </Card>
      )}

      <Question label="Are you a third-party payer company? (Yes = complete Section 6B)">
        <FormikRadioYN name="third_party_payer_comp" aria-label="Third-party payer company" />
      </Question>

      {values.third_party_payer_comp === "Y" && (
        <Card className="flex flex-col gap-4 p-5">
          <span className="text-foreground text-sm font-semibold">6B — Third-Party Payer Companies</span>
          <Text name="tp_handle_contact" label="1. Who handled the contacts with the client?" maxLength={30} />
          <Text name="tp_client_busn" label="2. Who was your contact at the client business?" maxLength={30} />
          <Text name="tp_agrm_for_service" label="3. Who at the client signed the service agreement?" maxLength={30} />
          <Text name="tp_ctrl_emp_tax" label="4. Who controlled payment of the client's employment taxes?" maxLength={30} />
          <Text name="tp_fund_mad_avail" label="5. How were funds made available from the client?" maxLength={30} />
          <div className="grid grid-cols-2 gap-4">
            <Text name="tp_bnk_name" label="Bank name" maxLength={30} />
            <Text name="tp_accn_no" label="Account number" maxLength={30} autoComplete="off" />
          </div>
          <Question label="6. Were funds actually available for you to make the tax payments?">
            <FormikRadioYN name="tp_make_pay_tax" aria-label="Funds available to you" />
            {values.tp_make_pay_tax === "Y" && (
              <FormikTextarea name="tp_make_pay_tax_exp" aria-label="Explain transfer" rows={2} />
            )}
            {values.tp_make_pay_tax === "N" && (
              <FormikTextarea name="tp_make_pay_tax_action" aria-label="Actions to collect funds" rows={2} />
            )}
          </Question>
        </Card>
      )}
    </div>
  );
}

// --- Section 7 — Excise-Tax Responsibility ----------------------------------

export function ExciseSection7() {
  const { values } = useFormikContext<Form4180Data>();
  return (
    <div className="flex flex-col gap-6">
      <Question label="Is the business required to file Excise Tax Returns? (Yes = complete questions 2–5)">
        <FormikRadioYN name="resp_excise_tax" aria-label="Responsible for excise taxes" />
      </Question>
      {values.resp_excise_tax === "Y" && (
        <>
          <Question label="1. Are you aware of any required excise tax returns which have not been filed?">
            <FormikRadioGroup name="et_ret_not_fill" aria-label="Unfiled excise returns" options={YNU} />
          </Question>
          <Question label="2. Were patrons/customers informed the tax was included in the sales price?">
            <FormikRadioGroup name="et_inc_sale_price" aria-label="Included in sale price" options={YNU} />
          </Question>
          <Question label="3. If a 'collected' tax, was the tax collected?">
            <FormikRadioGroup name="et_tax_collect" aria-label="Tax collected" options={YNU} />
          </Question>
          <Question label="4. Were you aware, during the period, that the law required collection of the tax?">
            <FormikRadioGroup name="et_law_req" aria-label="Aware law required collection" options={YNU} />
          </Question>
        </>
      )}
    </div>
  );
}

// --- Section 8 — Your Story -------------------------------------------------

export function ExciseSection8() {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        Use the space below to explain anything that helps us understand your role
        and the circumstances.
      </p>
      <FormikTextarea name="add_info" aria-label="Additional information" rows={10} />
    </div>
  );
}
